import { Check, ChevronDown, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface FilterOption {
  value: string;
  /** What to show; the value itself when absent. */
  label?: string;
  /** How many rows in the current filter carry this value, when known. */
  count?: number;
}

interface FilterMultiSelectProps {
  label: string;
  options: FilterOption[];
  selected: readonly string[];
  onChange: (values: string[]) => void;
  /** Show a search box inside the menu; automatic above this many options. */
  searchable?: boolean;
  /** Names the trigger in tests and analytics. */
  testId?: string;
  className?: string;
  /** Options are still loading; the menu says so instead of "no matches". */
  loading?: boolean;
  /** A short line under the search box, e.g. "Top 60 by count". */
  hint?: string;
  /** Called when the menu opens; a facet that is expensive to count loads then. */
  onOpen?: () => void;
}

const SEARCH_ABOVE = 8;

/**
 * A checkbox list behind a button, with a search box once the list is long.
 *
 * Built on the dropdown menu the rest of the Bridge uses rather than a select:
 * a select is one value, and every filter here is "any of these". The counts
 * beside each option are the facet counts for the CURRENT filter set, so the
 * menu says "Germany (60,012)" and never offers a choice that would empty the
 * page.
 *
 * A value that is selected but no longer among the options (a facet that fell
 * to zero, or a value typed into the URL) is still listed, so it can be
 * unticked.
 */
export function FilterMultiSelect({
  label,
  options,
  selected,
  onChange,
  searchable,
  testId,
  className,
  loading = false,
  hint,
  onOpen,
}: FilterMultiSelectProps): JSX.Element {
  const [query, setQuery] = useState('');
  const showSearch = searchable ?? options.length > SEARCH_ABOVE;

  const allOptions = useMemo(() => {
    const known = new Set(options.map((o) => o.value));
    const extra: FilterOption[] = selected.filter((v) => !known.has(v)).map((value) => ({ value }));
    return [...extra, ...options];
  }, [options, selected]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return allOptions;
    return allOptions.filter((o) => (o.label ?? o.value).toLowerCase().includes(q));
  }, [allOptions, query]);

  const toggle = (value: string): void => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const summary =
    selected.length === 0
      ? label
      : selected.length === 1
        ? `${label}: ${allOptions.find((o) => o.value === selected[0])?.label ?? selected[0] ?? ''}`
        : `${label}: ${String(selected.length)}`;

  return (
    <DropdownMenu onOpenChange={(open) => { if (open) onOpen?.(); else setQuery(''); }}>
      <DropdownMenuTrigger asChild>
        <Button
          variant={selected.length > 0 ? 'secondary' : 'outline'}
          size="sm"
          className={cn('max-w-[16rem] justify-between gap-1 font-normal', className)}
          data-testid={testId}
          data-active={selected.length > 0 ? 'true' : undefined}
        >
          <span className="truncate">{summary}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 p-0" data-testid={testId ? `${testId}-menu` : undefined}>
        {showSearch && (
          <div className="border-b p-2">
            <Input
              autoFocus
              placeholder={`Find ${label.toLowerCase()}…`}
              value={query}
              onChange={(e) => { setQuery(e.target.value); }}
              // The menu's typeahead would otherwise eat every keystroke.
              onKeyDown={(e) => { e.stopPropagation(); }}
              className="h-8"
              data-testid={testId ? `${testId}-search` : undefined}
            />
            {hint !== undefined && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
          </div>
        )}
        <div className="max-h-72 overflow-y-auto py-1">
          {loading && visible.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">Loading…</div>
          )}
          {!loading && visible.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">No matches</div>
          )}
          {visible.map((option) => {
            const checked = selected.includes(option.value);
            return (
              <DropdownMenuItem
                key={option.value}
                onSelect={(e) => { e.preventDefault(); toggle(option.value); }}
                className="gap-2"
                data-testid={testId ? `${testId}-option` : undefined}
                data-value={option.value}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                    checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input'
                  )}
                  aria-hidden
                >
                  {checked && <Check className="h-3 w-3" />}
                </span>
                <span className="flex-1 truncate">{option.label ?? option.value}</span>
                {option.count !== undefined && (
                  <span className="text-xs tabular-nums text-muted-foreground">{option.count.toLocaleString()}</span>
                )}
              </DropdownMenuItem>
            );
          })}
        </div>
        {selected.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => { e.preventDefault(); onChange([]); }}
              className="gap-2 text-muted-foreground"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X className="h-3.5 w-3.5" />
              Clear {label.toLowerCase()}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
